import React from 'react';
import { Skeleton, Chip } from '@mui/material';
import { ShowChart, TrendingUp } from '@mui/icons-material';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';

const formatCurrency = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg rounded-xl border border-gray-100 p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{formatCurrency(p.value || 0)}</strong>
        </p>
      ))}
    </div>
  );
};

const IndemnityForecastSection = ({ data = [], summary = {}, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <Skeleton variant="text" width={200} height={28} />
        <Skeleton variant="rounded" height={260} sx={{ mt: 2 }} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center gap-2 mb-4">
        <ShowChart sx={{ fontSize: 18, color: '#6366f1' }} />
        <h3 className="text-sm font-semibold text-gray-700">Indemnity Forecast</h3>
        <Chip
          label="Forecast"
          size="small"
          icon={<TrendingUp sx={{ fontSize: 12 }} />}
          sx={{
            ml: 1,
            fontSize: 10,
            height: 20,
            backgroundColor: '#eef2ff',
            color: '#6366f1',
            '& .MuiChip-icon': { color: '#6366f1' },
          }}
        />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div className="bg-indigo-50 rounded-lg p-2 text-center">
          <p className="text-[10px] text-indigo-500 font-medium">Paid YTD</p>
          <p className="text-sm font-bold text-indigo-700">{formatCurrency(summary.totalPaidYTD || 0)}</p>
        </div>
        <div className="bg-violet-50 rounded-lg p-2 text-center">
          <p className="text-[10px] text-violet-500 font-medium">Forecast (3M)</p>
          <p className="text-sm font-bold text-violet-700">{formatCurrency(summary.forecastNextQtr || 0)}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-2 text-center">
          <p className="text-[10px] text-blue-500 font-medium">Avg Payout</p>
          <p className="text-sm font-bold text-blue-700">{formatCurrency(summary.avgPayout || 0)}</p>
        </div>
        <div className="bg-rose-50 rounded-lg p-2 text-center">
          <p className="text-[10px] text-rose-500 font-medium">Exp. Loss Ratio</p>
          <p className="text-sm font-bold text-rose-700">{(summary.expectedLossRatio || 0).toFixed(1)}%</p>
        </div>
      </div>

      {/* Chart */}
      {data.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-sm text-gray-400">
          No indemnity data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => formatCurrency(v)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
            />

            {/* Vertical line separating actual from forecast */}
            {data.length > 6 && (
              <ReferenceLine
                x={data[5]?.month}
                stroke="#c7d2fe"
                strokeDasharray="4 4"
                label={{ value: 'Today', fontSize: 9, fill: '#a5b4fc', position: 'top' }}
              />
            )}

            <Bar
              dataKey="actual"
              name="Actual Payout"
              fill="#6366f1"
              radius={[4, 4, 0, 0]}
              barSize={16}
            />
            <Bar
              dataKey="reserved"
              name="Reserved"
              fill="#c7d2fe"
              radius={[4, 4, 0, 0]}
              barSize={16}
            />
            <Line
              dataKey="forecast"
              name="Forecast"
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={{ r: 3, fill: '#f59e0b' }}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default IndemnityForecastSection;
